// src/components/Dashboard/WidgetTogglePanel.tsx
// Slide-in customise panel — lists all widgets the current tier can access.
// Lower-tier widgets are hidden entirely (not greyed out) — per spec.
import { X, SlidersHorizontal } from 'lucide-react'
import {
  WIDGET_CATALOGUE,
  WidgetId,
  canAccessTier,
} from './types'

interface Props {
  userTier: string
  prefs:    Partial<Record<WidgetId, boolean>>
  onChange: (id: WidgetId, visible: boolean) => void
  onClose:  () => void
}

export function WidgetTogglePanel({ userTier, prefs, onChange, onClose }: Props) {
  const accessible = WIDGET_CATALOGUE.filter(w => canAccessTier(userTier, w.minTier))

  const isOn = (id: WidgetId): boolean => {
    const cfg = WIDGET_CATALOGUE.find(w => w.id === id)!
    return id in prefs ? (prefs[id] as boolean) : cfg.defaultOn
  }

  const activeCount = accessible.filter(w => isOn(w.id)).length

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40 bg-black/25"
        aria-hidden="true"
        onClick={onClose}
      />

      {/* Panel */}
      <div className="fixed right-0 top-0 h-full z-50 w-80 max-w-full bg-white shadow-2xl flex flex-col">
        {/* Header */}
        <div className="flex items-start justify-between px-5 py-4 border-b bg-white sticky top-0">
          <div>
            <div className="flex items-center gap-2">
              <SlidersHorizontal className="w-4 h-4 text-blue-600" />
              <h2 className="font-semibold text-gray-900 text-sm">Customise dashboard</h2>
            </div>
            <p className="text-xs text-gray-500 mt-0.5">
              {activeCount} of {accessible.length} widgets active. Changes save automatically.
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Widget list */}
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-2">
          {accessible.map(cfg => {
            const on = isOn(cfg.id)
            return (
              <button
                key={cfg.id}
                onClick={() => onChange(cfg.id, !on)}
                className={`w-full flex items-center gap-3 px-3.5 py-3 rounded-xl border text-left transition-all ${
                  on
                    ? 'border-blue-200 bg-blue-50 hover:bg-blue-100'
                    : 'border-gray-200 bg-white hover:bg-gray-50'
                }`}
              >
                {/* Toggle pill */}
                <div className={`relative w-9 h-5 rounded-full flex-shrink-0 transition-colors ${on ? 'bg-blue-600' : 'bg-gray-200'}`}>
                  <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${on ? 'translate-x-4' : 'translate-x-0.5'}`} />
                </div>

                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-medium leading-tight ${on ? 'text-blue-900' : 'text-gray-700'}`}>
                    {cfg.label}
                  </p>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {cfg.colSpan === 12 ? 'Full width' : cfg.colSpan === 6 ? 'Half width' : cfg.colSpan === 4 ? 'Third width' : 'Quarter width'}
                    {cfg.defaultOn ? ' · On by default' : ''}
                  </p>
                </div>
              </button>
            )
          })}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t bg-gray-50 text-xs text-gray-400">
          Widgets not available to your tier are hidden. Drag-and-drop reordering available in Sprint 6.
        </div>
      </div>
    </>
  )
}
